import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    TrendingUp, PieChart as PieChartIcon, Scale, Home as HomeIcon,
    ArrowRight, ChevronDown, RotateCcw,
} from "lucide-react";
import {
    BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { fmtCurrency, fmtCompact } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// Same categorical hues already used elsewhere in the app (see lib/format.js's
// CATEGORY_COLORS) — kept fixed and never remapped to the brand accent, so these
// charts stay colorblind-distinguishable regardless of which theme accent a user picks.
const NET_WORTH_COLORS = { cash: "#64748B", invest: "#6366F1", property: "#10B981", vehicles: "#F59E0B", other: "#0EA5E9" };
const MORTGAGE_COLORS = { principal: "#10B981", interest: "#6366F1", taxesFees: "#F59E0B" };

// Each tab gets its own distinct color at rest (so the bar reads as "colorful"
// rather than a flat row of gray icons); the *selected* tab instead switches to
// bg-indigo-600 — which index.css remaps to the user's chosen accent color, so the
// active tab always matches whatever color the app's primary buttons are using.
const TOOLS = [
    { value: "investment", label: "Investment Growth", icon: TrendingUp, color: "#10B981" },
    { value: "budget", label: "50/30/20 Budget", icon: PieChartIcon, color: "#F59E0B" },
    { value: "networth", label: "Net Worth", icon: Scale, color: "#8B5CF6" },
    { value: "mortgage", label: "Mortgage", icon: HomeIcon, color: "#0EA5E9" },
];

export default function Tools() {
    const [tab, setTab] = useState("investment");

    return (
        <div className="space-y-4" data-testid="tools-page">
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-slate-100 rounded-xl p-1 h-auto flex-nowrap justify-start gap-1 w-full overflow-x-auto no-scrollbar lg:w-auto lg:flex-wrap" data-testid="tools-tabs">
                    {TOOLS.map(t => (
                        <TabsTrigger
                            key={t.value} value={t.value} data-testid={`tools-tab-${t.value}`}
                            className="rounded-lg gap-1.5 px-3 py-2 text-slate-600 hover:text-slate-900 flex-shrink-0 whitespace-nowrap data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-colors"
                        >
                            <t.icon className="w-3.5 h-3.5 flex-shrink-0" style={tab === t.value ? undefined : { color: t.color }} />
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="investment" className="mt-4"><InvestmentGrowthCalculator /></TabsContent>
                <TabsContent value="budget" className="mt-4"><BudgetSplitCalculator /></TabsContent>
                <TabsContent value="networth" className="mt-4"><NetWorthCalculator /></TabsContent>
                <TabsContent value="mortgage" className="mt-4"><MortgageCalculator /></TabsContent>
            </Tabs>
        </div>
    );
}

function FieldL({ label, hint, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
            {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
        </div>
    );
}

function SplitBar({ segments }) {
    // segments: [{ color, pct }] — pct out of 100, already normalized by the caller.
    return (
        <div className="w-full h-3 rounded-full overflow-hidden flex bg-slate-100">
            {segments.map((s, i) => (
                <div key={i} className="h-full" style={{ width: `${s.pct}%`, backgroundColor: s.color }} />
            ))}
        </div>
    );
}

// ============================================================
// 1. Investment Growth Calculator
// ============================================================

// Simulates balance period-by-period (by compounding period) so any combination of
// compounding frequency and contribution frequency is handled correctly, rather than
// relying on a closed-form formula that assumes they match. Ordinary annuity
// convention: growth applied first each period, then that period's share of the
// annual contribution is added at period end. (Ported 1:1 from the marketing site's
// investment-calculator.html so results match exactly.)
function simulateInvestment(principal, annualRatePct, years, compoundsPerYear, contribAmount, contribPerYear) {
    const periodRate = (annualRatePct / 100) / compoundsPerYear;
    const totalPeriods = Math.round(compoundsPerYear * years);
    const annualContribution = contribAmount * contribPerYear;
    const contribPerPeriod = annualContribution / compoundsPerYear;

    let balance = principal;
    for (let i = 0; i < totalPeriods; i++) {
        balance = balance * (1 + periodRate) + contribPerPeriod;
    }

    const totalContributions = annualContribution * years;
    let interest = balance - principal - totalContributions;
    if (interest < 0) interest = 0; // guard against float rounding on 0% rate

    return { balance, principal, totalContributions, interest };
}

function InvestmentGrowthCalculator() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";

    const [initial, setInitial] = useState(10000);
    const [years, setYears] = useState(10);
    const [rate, setRate] = useState(7);
    const [compoundsPerYear, setCompoundsPerYear] = useState(12);
    const [contribAmount, setContribAmount] = useState(200);
    const [contribFreq, setContribFreq] = useState(12); // 12 = monthly, 1 = annually

    const r = useMemo(() => simulateInvestment(
        Math.max(0, initial || 0), Math.max(0, rate || 0), Math.max(0, years || 0),
        compoundsPerYear, Math.max(0, contribAmount || 0), contribFreq,
    ), [initial, years, rate, compoundsPerYear, contribAmount, contribFreq]);

    const total = r.principal + r.totalContributions + r.interest;
    const pPct = total > 0 ? (r.principal / total) * 100 : 0;
    const cPct = total > 0 ? (r.totalContributions / total) * 100 : 0;
    const iPct = total > 0 ? (r.interest / total) * 100 : 0;

    return (
        <div className="grid lg:grid-cols-5 gap-6 items-start" data-testid="tool-investment">
            <div className="lg:col-span-3 card-elev rounded-2xl p-6">
                <h2 className="text-base font-display font-bold text-slate-900 mb-5">Investment details</h2>
                <div className="space-y-4">
                    <FieldL label="Initial deposit">
                        <Input type="number" min="0" step="1" value={initial} onChange={e => setInitial(parseFloat(e.target.value))} data-testid="inv-initial" />
                    </FieldL>
                    <FieldL label="Years of growth">
                        <Input type="number" min="1" max="80" step="1" value={years} onChange={e => setYears(parseFloat(e.target.value))} data-testid="inv-years" />
                    </FieldL>
                    <FieldL label="Estimated rate of return" hint="The S&P 500's long-run historical average is roughly 7–10% annually.">
                        <div className="relative">
                            <Input type="number" min="0" max="100" step="0.1" value={rate} onChange={e => setRate(parseFloat(e.target.value))} className="pr-8" data-testid="inv-rate" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">%</span>
                        </div>
                    </FieldL>
                    <FieldL label="Compound frequency">
                        <Select value={String(compoundsPerYear)} onValueChange={v => setCompoundsPerYear(parseInt(v, 10))}>
                            <SelectTrigger data-testid="inv-compound"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Annually</SelectItem>
                                <SelectItem value="2">Semi-annually</SelectItem>
                                <SelectItem value="4">Quarterly</SelectItem>
                                <SelectItem value="12">Monthly</SelectItem>
                                <SelectItem value="365">Daily</SelectItem>
                            </SelectContent>
                        </Select>
                    </FieldL>
                    <FieldL label="Contribution amount">
                        <Input type="number" min="0" step="1" value={contribAmount} onChange={e => setContribAmount(parseFloat(e.target.value))} data-testid="inv-contribution" />
                    </FieldL>
                    <FieldL label="Contribution frequency">
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setContribFreq(12)} data-testid="inv-freq-monthly"
                                className={`px-3 py-2 rounded-xl border text-sm font-semibold transition-colors ${contribFreq === 12 ? "border-indigo-600 bg-indigo-600 text-white shadow-sm" : "border-slate-300 text-slate-700 hover:border-indigo-400"}`}>
                                Monthly
                            </button>
                            <button type="button" onClick={() => setContribFreq(1)} data-testid="inv-freq-annually"
                                className={`px-3 py-2 rounded-xl border text-sm font-semibold transition-colors ${contribFreq === 1 ? "border-indigo-600 bg-indigo-600 text-white shadow-sm" : "border-slate-300 text-slate-700 hover:border-indigo-400"}`}>
                                Annually
                            </button>
                        </div>
                    </FieldL>
                </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
                <div className="card-elev rounded-2xl p-6 text-center">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Future balance</p>
                    <p className="text-3xl font-display font-extrabold text-slate-900 tabular-nums" data-testid="inv-out-total">{fmtCurrency(r.balance, cur)}</p>
                    <p className="text-sm text-slate-500 mt-2">after {years} years</p>
                </div>
                <div className="card-elev rounded-2xl p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-4">Where it came from</p>
                    <SplitBar segments={[
                        { color: "#94A3B8", pct: pPct },
                        { color: "#4F46E5", pct: cPct },
                        { color: "#10B981", pct: iPct },
                    ]} />
                    <ul className="space-y-2.5 text-sm mt-4">
                        <li className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" />Initial deposit</span>
                            <span className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.principal, cur)}</span>
                        </li>
                        <li className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-indigo-600" />Total contributions</span>
                            <span className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.totalContributions, cur)}</span>
                        </li>
                        <li className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Interest earned</span>
                            <span className="font-bold text-emerald-600 tabular-nums">{fmtCurrency(r.interest, cur)}</span>
                        </li>
                    </ul>
                </div>
                <Link to="/investments" className="card-elev rounded-2xl p-5 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition-colors group">
                    <div>
                        <p className="font-display font-bold text-sm text-slate-900">Have real accounts to track?</p>
                        <p className="text-xs text-slate-500 mt-0.5">Log actual contributions and growth in Investments.</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </Link>
            </div>
        </div>
    );
}

// ============================================================
// 2. 50/30/20 Budget Calculator
// ============================================================

const PAY_FREQUENCIES = [
    { value: "1", label: "Monthly" },
    { value: "2.166667", label: "Bi-weekly (26/yr)" },
    { value: "4.333333", label: "Weekly (52/yr)" },
    { value: "0.083333", label: "Annual" },
];

function BudgetSplitCalculator() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";

    const [income, setIncome] = useState(5000);
    const [freqFactor, setFreqFactor] = useState("1");
    const [needs, setNeeds] = useState(50);
    const [wants, setWants] = useState(30);
    const [savings, setSavings] = useState(20);

    const monthlyIncome = Math.max(0, income || 0) * (parseFloat(freqFactor) || 1);
    const pNeeds = Math.max(0, needs || 0), pWants = Math.max(0, wants || 0), pSavings = Math.max(0, savings || 0);
    const total = pNeeds + pWants + pSavings;
    const offBy100 = Math.abs(total - 100) > 0.01;

    const needsAmt = monthlyIncome * (pNeeds / 100);
    const wantsAmt = monthlyIncome * (pWants / 100);
    const savingsAmt = monthlyIncome * (pSavings / 100);

    // Bar widths are proportional to each other even if the total isn't exactly 100,
    // so the visual always reflects the actual ratio entered.
    const barTotal = pNeeds + pWants + pSavings;
    const nPct = barTotal > 0 ? (pNeeds / barTotal) * 100 : 0;
    const wPct = barTotal > 0 ? (pWants / barTotal) * 100 : 0;
    const sPct = barTotal > 0 ? (pSavings / barTotal) * 100 : 0;

    const reset = () => { setNeeds(50); setWants(30); setSavings(20); };

    return (
        <div className="grid lg:grid-cols-5 gap-6 items-start" data-testid="tool-budget">
            <div className="lg:col-span-3 card-elev rounded-2xl p-6">
                <h2 className="text-base font-display font-bold text-slate-900 mb-5">Your income</h2>
                <div className="space-y-4">
                    <FieldL label="Monthly take-home pay" hint="Use net income (after taxes), not gross salary.">
                        <Input type="number" min="0" step="1" value={income} onChange={e => setIncome(parseFloat(e.target.value))} data-testid="budget-income" />
                    </FieldL>
                    <FieldL label="Pay entered above is" hint="Everything below is converted to a monthly figure.">
                        <Select value={freqFactor} onValueChange={setFreqFactor}>
                            <SelectTrigger data-testid="budget-freq"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PAY_FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </FieldL>
                    <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                            <Label className="text-xs font-semibold text-slate-700">Split ratio</Label>
                            <button type="button" onClick={reset} data-testid="budget-reset" className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                                <RotateCcw className="w-3 h-3" /> Reset to 50/30/20
                            </button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <FieldL label={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-500" />Needs</span>}>
                                <Input type="number" min="0" max="100" step="1" value={needs} onChange={e => setNeeds(parseFloat(e.target.value))} data-testid="budget-needs" />
                            </FieldL>
                            <FieldL label={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-indigo-600" />Wants</span>}>
                                <Input type="number" min="0" max="100" step="1" value={wants} onChange={e => setWants(parseFloat(e.target.value))} data-testid="budget-wants" />
                            </FieldL>
                            <FieldL label={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Savings</span>}>
                                <Input type="number" min="0" max="100" step="1" value={savings} onChange={e => setSavings(parseFloat(e.target.value))} data-testid="budget-savings" />
                            </FieldL>
                        </div>
                        {offBy100 && (
                            <p className="mt-2 text-xs font-semibold text-amber-600" data-testid="budget-split-warning">
                                Your split adds up to {total}%, not 100% — amounts below use it as-is, but double check it's what you meant.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
                <div className="card-elev rounded-2xl p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-4">Monthly split</p>
                    <SplitBar segments={[
                        { color: "#64748B", pct: nPct },
                        { color: "#4F46E5", pct: wPct },
                        { color: "#10B981", pct: sPct },
                    ]} />
                    <ul className="space-y-3 text-sm mt-4">
                        <li className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-slate-500" />Needs <span className="text-slate-400">({pNeeds}%)</span></span>
                            <span className="font-bold text-slate-900 tabular-nums">{fmtCurrency(needsAmt, cur)}</span>
                        </li>
                        <li className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-indigo-600" />Wants <span className="text-slate-400">({pWants}%)</span></span>
                            <span className="font-bold text-slate-900 tabular-nums">{fmtCurrency(wantsAmt, cur)}</span>
                        </li>
                        <li className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Savings &amp; debt <span className="text-slate-400">({pSavings}%)</span></span>
                            <span className="font-bold text-emerald-600 tabular-nums">{fmtCurrency(savingsAmt, cur)}</span>
                        </li>
                    </ul>
                </div>
                <Link to="/budgets" className="card-elev rounded-2xl p-5 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition-colors group">
                    <div>
                        <p className="font-display font-bold text-sm text-slate-900">Want this as a real budget?</p>
                        <p className="text-xs text-slate-500 mt-0.5">Turn this split into category budgets in Budget Flow.</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </Link>
            </div>
        </div>
    );
}

// ============================================================
// 3. Net Worth Calculator
// ============================================================

function NetWorthCalculator() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";

    const [cash, setCash] = useState(15000);
    const [invest, setInvest] = useState(45000);
    const [property, setProperty] = useState(350000);
    const [vehicles, setVehicles] = useState(20000);
    const [otherAssets, setOtherAssets] = useState(5000);

    const [mortgage, setMortgage] = useState(280000);
    const [auto, setAuto] = useState(12000);
    const [student, setStudent] = useState(18000);
    const [otherDebt, setOtherDebt] = useState(3000);

    const n = v => Math.max(0, v || 0);
    const totalAssets = n(cash) + n(invest) + n(property) + n(vehicles) + n(otherAssets);
    const totalLiabilities = n(mortgage) + n(auto) + n(student) + n(otherDebt);
    const netWorth = totalAssets - totalLiabilities;
    const maxSide = Math.max(totalAssets, totalLiabilities, 1);

    const donutData = [
        { name: "Cash", value: n(cash), color: NET_WORTH_COLORS.cash },
        { name: "Investments", value: n(invest), color: NET_WORTH_COLORS.invest },
        { name: "Property", value: n(property), color: NET_WORTH_COLORS.property },
        { name: "Vehicles", value: n(vehicles), color: NET_WORTH_COLORS.vehicles },
        { name: "Other", value: n(otherAssets), color: NET_WORTH_COLORS.other },
    ].filter(d => d.value > 0);

    return (
        <div className="grid lg:grid-cols-5 gap-6 items-start" data-testid="tool-networth">
            <div className="lg:col-span-3 space-y-4">
                <div className="card-elev rounded-2xl p-6">
                    <h2 className="flex items-center gap-2 text-base font-display font-bold text-slate-900 mb-5">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Assets — what you own
                    </h2>
                    <div className="space-y-3">
                        <NetWorthRow label="Cash & savings" value={cash} onChange={setCash} testid="nw-cash" />
                        <NetWorthRow label="Investments & retirement" value={invest} onChange={setInvest} testid="nw-invest" />
                        <NetWorthRow label="Real estate value" value={property} onChange={setProperty} testid="nw-property" />
                        <NetWorthRow label="Vehicles" value={vehicles} onChange={setVehicles} testid="nw-vehicles" />
                        <NetWorthRow label="Other assets" value={otherAssets} onChange={setOtherAssets} testid="nw-other-assets" />
                    </div>
                </div>
                <div className="card-elev rounded-2xl p-6">
                    <h2 className="flex items-center gap-2 text-base font-display font-bold text-slate-900 mb-5">
                        <span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Liabilities — what you owe
                    </h2>
                    <div className="space-y-3">
                        <NetWorthRow label="Mortgage balance" value={mortgage} onChange={setMortgage} testid="nw-mortgage" />
                        <NetWorthRow label="Auto loans" value={auto} onChange={setAuto} testid="nw-auto" />
                        <NetWorthRow label="Student loans" value={student} onChange={setStudent} testid="nw-student" />
                        <NetWorthRow label="Credit cards & other debt" value={otherDebt} onChange={setOtherDebt} testid="nw-other-debt" />
                    </div>
                </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
                <div className="card-elev rounded-2xl p-6 text-center">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Your net worth</p>
                    <p className="text-3xl font-display font-extrabold text-slate-900 tabular-nums" data-testid="nw-out-networth">{fmtCurrency(netWorth, cur)}</p>
                    <p className="mt-2 text-xs text-slate-400">Total assets minus total liabilities</p>
                </div>

                <div className="card-elev rounded-2xl p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-4">Assets vs. liabilities</p>
                    <div className="space-y-3">
                        <div>
                            <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1">
                                <span>Assets</span><span className="tabular-nums">{fmtCurrency(totalAssets, cur)}</span>
                            </div>
                            <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(totalAssets / maxSide) * 100}%` }} /></div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1">
                                <span>Liabilities</span><span className="tabular-nums">{fmtCurrency(totalLiabilities, cur)}</span>
                            </div>
                            <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-rose-500 rounded-full" style={{ width: `${(totalLiabilities / maxSide) * 100}%` }} /></div>
                        </div>
                    </div>
                </div>

                <div className="card-elev rounded-2xl p-6">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-4">Where your assets are</p>
                    {donutData.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">Add an asset above to see the breakdown.</p>
                    ) : (
                        <div className="flex items-center gap-5">
                            <div className="w-28 h-28 shrink-0 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={38} outerRadius={54} paddingAngle={2.5} stroke="none">
                                            {donutData.map(d => <Cell key={d.name} fill={d.color} />)}
                                        </Pie>
                                        <Tooltip formatter={(v, nm) => [fmtCurrency(v, cur), nm]} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="text-xs font-semibold text-slate-600 space-y-1.5 flex-1">
                                {donutData.map(d => (
                                    <li key={d.name} className="flex items-center justify-between">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: d.color }} />{d.name}</span>
                                        <span>{totalAssets > 0 ? Math.round((d.value / totalAssets) * 100) : 0}%</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function NetWorthRow({ label, value, onChange, testid }) {
    return (
        <div className="grid grid-cols-2 gap-3 items-center">
            <Label className="text-sm font-medium text-slate-700">{label}</Label>
            <Input type="number" min="0" step="1" value={value} onChange={e => onChange(parseFloat(e.target.value))} data-testid={testid} />
        </div>
    );
}

// ============================================================
// 4. Mortgage Calculator
// ============================================================

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Ported 1:1 from the marketing site's mortgage-calculator.html.
function amortize({ homeValue, downPayment, rate, termYears, annualTax, pmiRate, annualInsurance, hoa }) {
    const dp = Math.min(downPayment, homeValue);
    const loanAmount = Math.max(0, homeValue - dp);
    const monthlyRate = (rate / 100) / 12;
    const n = Math.max(1, Math.round(termYears * 12));
    let M;
    if (loanAmount <= 0) {
        M = 0;
    } else if (monthlyRate === 0) {
        M = loanAmount / n;
    } else {
        const f = Math.pow(1 + monthlyRate, n);
        M = loanAmount * (monthlyRate * f) / (f - 1);
    }

    const downPct = homeValue > 0 ? (dp / homeValue) * 100 : 0;
    const pmiRequired = downPct < 20 && loanAmount > 0;
    const pmiThreshold = 0.8 * homeValue;
    const monthlyPMI = pmiRequired ? (pmiRate / 100 / 12) * loanAmount : 0;
    const monthlyTax = annualTax / 12;
    const monthlyIns = annualInsurance / 12;

    let balance = loanAmount;
    let totalInterest = 0;
    let pmiCancelMonth = null;
    const yearly = [];
    let yp = 0, yi = 0, ytf = 0;

    for (let m = 1; m <= n; m++) {
        const interest = balance * monthlyRate;
        let principal = M - interest;
        if (m === n || principal > balance) principal = balance;
        balance = Math.max(0, balance - principal);
        totalInterest += interest;

        let pmiThisMonth = 0;
        if (pmiRequired && balance > pmiThreshold) {
            pmiThisMonth = monthlyPMI;
        } else if (pmiRequired && pmiCancelMonth === null) {
            pmiCancelMonth = m;
        }

        yp += principal; yi += interest; ytf += monthlyTax + monthlyIns + hoa + pmiThisMonth;

        if (m % 12 === 0 || m === n) {
            yearly.push({ principal: yp, interest: yi, taxesFees: ytf, endBalance: balance });
            yp = 0; yi = 0; ytf = 0;
        }
    }

    const firstMonthPMI = pmiRequired ? monthlyPMI : 0;
    const totalMonthlyPayment = M + monthlyTax + monthlyIns + hoa + firstMonthPMI;

    return {
        loanAmount, monthlyPI: M, downPayment: dp, downPct,
        pmiRequired, monthlyPMI: firstMonthPMI, pmiCancelMonth,
        totalMonthlyPayment, totalInterest, monthlyTax, monthlyIns, n, yearly,
    };
}

function MortgageCalculator() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";

    const [homeValue, setHomeValue] = useState(400000);
    const [downMode, setDownMode] = useState("dollar");
    const [down, setDown] = useState(80000);
    const [rate, setRate] = useState(6.48);
    const [termYears, setTermYears] = useState(30);
    const [startMonth, setStartMonth] = useState(7); // August
    const [startYear, setStartYear] = useState(2026);
    const [annualTax, setAnnualTax] = useState(3000);
    const [annualInsurance, setAnnualInsurance] = useState(1500);
    const [pmiRate, setPmiRate] = useState(0.5);
    const [hoa, setHoa] = useState(0);
    const [showTable, setShowTable] = useState(false);

    const toggleDownMode = (mode) => {
        if (mode === downMode) return;
        const hv = Math.max(0, homeValue || 0);
        if (mode === "percent") {
            const dollarVal = Math.max(0, down || 0);
            setDown(hv > 0 ? +((dollarVal / hv) * 100).toFixed(2) : 0);
        } else {
            const pctVal = Math.max(0, down || 0);
            setDown(Math.round(hv * (pctVal / 100)));
        }
        setDownMode(mode);
    };

    const downPaymentDollars = useMemo(() => {
        const hv = Math.max(0, homeValue || 0);
        const v = Math.max(0, down || 0);
        return downMode === "percent" ? hv * (v / 100) : v;
    }, [homeValue, down, downMode]);

    const r = useMemo(() => {
        const result = amortize({
            homeValue: Math.max(0, homeValue || 0),
            downPayment: downPaymentDollars,
            rate: Math.max(0, rate || 0),
            termYears: Math.max(1, termYears || 1),
            annualTax: Math.max(0, annualTax || 0),
            pmiRate: Math.max(0, pmiRate || 0),
            annualInsurance: Math.max(0, annualInsurance || 0),
            hoa: Math.max(0, hoa || 0),
        });
        result.yearly.forEach((y, idx) => { y.calendarYear = startYear + idx + 1; });
        return result;
    }, [homeValue, downPaymentDollars, rate, termYears, annualTax, pmiRate, annualInsurance, hoa, startYear]);

    const payoffMonthIdx = startMonth + r.n;
    const payoffYear = startYear + Math.floor(payoffMonthIdx / 12);
    const payoffMonth = payoffMonthIdx % 12;
    const payoffDate = `${MONTHS[payoffMonth]} ${payoffYear}`;

    const balancePoints = useMemo(() => {
        if (r.yearly.length === 0) return [];
        const pts = [{ year: r.yearly[0].calendarYear - 1, balance: r.loanAmount }];
        r.yearly.forEach(y => pts.push({ year: y.calendarYear, balance: y.endBalance }));
        return pts;
    }, [r.yearly, r.loanAmount]);

    return (
        <div className="space-y-6" data-testid="tool-mortgage">
            <div className="grid lg:grid-cols-5 gap-6 items-start">
                <div className="lg:col-span-3 card-elev rounded-2xl p-6">
                    <h2 className="text-base font-display font-bold text-slate-900 mb-5">Loan details</h2>
                    <div className="space-y-4">
                        <FieldL label="Home value">
                            <Input type="number" min="0" step="1000" value={homeValue} onChange={e => setHomeValue(parseFloat(e.target.value))} data-testid="mtg-home-value" />
                        </FieldL>

                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <Label className="text-xs font-semibold text-slate-700">Down payment</Label>
                                <div className="flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 rounded-lg p-0.5">
                                    <button type="button" onClick={() => toggleDownMode("dollar")} data-testid="mtg-down-dollar"
                                        className={`px-2.5 py-1 rounded-md transition-colors ${downMode === "dollar" ? "bg-white shadow-sm text-slate-900" : ""}`}>$</button>
                                    <button type="button" onClick={() => toggleDownMode("percent")} data-testid="mtg-down-percent"
                                        className={`px-2.5 py-1 rounded-md transition-colors ${downMode === "percent" ? "bg-white shadow-sm text-slate-900" : ""}`}>%</button>
                                </div>
                            </div>
                            <Input type="number" min="0" step="1" value={down} onChange={e => setDown(parseFloat(e.target.value))} data-testid="mtg-down" />
                            <p className="mt-1.5 text-xs text-slate-400">Loan amount: <span className="font-semibold text-slate-600">{fmtCurrency(r.loanAmount, cur)}</span> · {r.downPct.toFixed(1).replace(/\.0$/, "")}% down</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <FieldL label="Interest rate">
                                <div className="relative">
                                    <Input type="number" min="0" max="25" step="0.01" value={rate} onChange={e => setRate(parseFloat(e.target.value))} className="pr-7" data-testid="mtg-rate" />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">%</span>
                                </div>
                            </FieldL>
                            <FieldL label="Loan term">
                                <div className="relative">
                                    <Input type="number" min="1" max="50" step="1" value={termYears} onChange={e => setTermYears(parseFloat(e.target.value))} className="pr-12" data-testid="mtg-term" />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-semibold pointer-events-none">years</span>
                                </div>
                            </FieldL>
                        </div>

                        <FieldL label="Start date">
                            <div className="grid grid-cols-2 gap-3">
                                <Select value={String(startMonth)} onValueChange={v => setStartMonth(parseInt(v, 10))}>
                                    <SelectTrigger data-testid="mtg-start-month"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {MONTHS.map((m, i) => <SelectItem key={m} value={String(i)}>{m}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Input type="number" min="2000" max="2100" step="1" value={startYear} onChange={e => setStartYear(parseFloat(e.target.value))} data-testid="mtg-start-year" />
                            </div>
                        </FieldL>

                        <div className="grid grid-cols-2 gap-3">
                            <FieldL label="Property tax">
                                <div className="relative">
                                    <Input type="number" min="0" step="1" value={annualTax} onChange={e => setAnnualTax(parseFloat(e.target.value))} className="pr-9" data-testid="mtg-tax" />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-semibold pointer-events-none">/yr</span>
                                </div>
                            </FieldL>
                            <FieldL label="Home insurance">
                                <div className="relative">
                                    <Input type="number" min="0" step="1" value={annualInsurance} onChange={e => setAnnualInsurance(parseFloat(e.target.value))} className="pr-9" data-testid="mtg-insurance" />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-semibold pointer-events-none">/yr</span>
                                </div>
                            </FieldL>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <FieldL label="PMI rate" hint="Only applies if down payment is under 20%.">
                                <div className="relative">
                                    <Input type="number" min="0" max="5" step="0.01" value={pmiRate} onChange={e => setPmiRate(parseFloat(e.target.value))} className="pr-7" data-testid="mtg-pmi-rate" />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold pointer-events-none">%</span>
                                </div>
                            </FieldL>
                            <FieldL label="Monthly HOA">
                                <Input type="number" min="0" step="1" value={hoa} onChange={e => setHoa(parseFloat(e.target.value))} data-testid="mtg-hoa" />
                            </FieldL>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2 space-y-4">
                    <div className="card-elev rounded-2xl p-6 text-center">
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">Total monthly payment</p>
                        <p className="text-3xl font-display font-extrabold text-slate-900 tabular-nums" data-testid="mtg-out-total">{fmtCurrency(r.totalMonthlyPayment, cur)}</p>
                        <p className="mt-2 text-xs text-slate-400">Principal, interest, taxes, insurance{r.pmiRequired ? ", PMI" : ""}, and HOA</p>
                    </div>
                    <div className="card-elev rounded-2xl p-6">
                        <dl className="grid grid-cols-2 gap-y-3.5 gap-x-3 text-sm">
                            <div><dt className="text-xs text-slate-400">Loan payoff date</dt><dd className="font-bold text-slate-900">{payoffDate}</dd></div>
                            <div><dt className="text-xs text-slate-400">Total interest paid</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.totalInterest, cur)}</dd></div>
                            <div><dt className="text-xs text-slate-400">Down payment</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.downPayment, cur)}</dd></div>
                            <div><dt className="text-xs text-slate-400">Down payment %</dt><dd className="font-bold text-slate-900">{r.downPct.toFixed(1).replace(/\.0$/, "")}%</dd></div>
                            <div><dt className="text-xs text-slate-400">PMI</dt><dd className="font-bold text-slate-900 tabular-nums">{r.pmiRequired ? `${fmtCurrency(r.monthlyPMI, cur)}/mo` : "Not required"}</dd></div>
                            <div><dt className="text-xs text-slate-400">Monthly tax</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.monthlyTax, cur)}</dd></div>
                            <div><dt className="text-xs text-slate-400">Total tax paid</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(annualTax * termYears, cur)}</dd></div>
                            <div><dt className="text-xs text-slate-400">Total insurance</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(annualInsurance * termYears, cur)}</dd></div>
                            <div><dt className="text-xs text-slate-400">Annual payment</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.totalMonthlyPayment * 12, cur)}</dd></div>
                            <div><dt className="text-xs text-slate-400">Total of all payments</dt><dd className="font-bold text-slate-900 tabular-nums">{fmtCurrency(r.totalMonthlyPayment * r.n, cur)}</dd></div>
                        </dl>
                    </div>
                </div>
            </div>

            <div className="card-elev rounded-2xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                    <h2 className="text-base font-display font-bold text-slate-900">Yearly payment breakdown</h2>
                    <div className="flex items-center gap-4 text-xs font-semibold text-slate-600">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: MORTGAGE_COLORS.principal }} />Principal</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: MORTGAGE_COLORS.interest }} />Interest</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: MORTGAGE_COLORS.taxesFees }} />Taxes &amp; fees</span>
                    </div>
                </div>
                <p className="text-xs text-slate-400 mb-4">How much of each year's payments goes to principal vs. interest vs. taxes, insurance, PMI, and HOA.</p>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={r.yearly} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                            <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="calendarYear" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                            <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => fmtCompact(v, cur)} />
                            <Tooltip
                                formatter={(value, name) => [fmtCurrency(value, cur), name]}
                                contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }}
                            />
                            <Bar dataKey="principal" name="Principal" stackId="pay" fill={MORTGAGE_COLORS.principal} />
                            <Bar dataKey="interest" name="Interest" stackId="pay" fill={MORTGAGE_COLORS.interest} />
                            <Bar dataKey="taxesFees" name="Taxes & fees" stackId="pay" fill={MORTGAGE_COLORS.taxesFees} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="card-elev rounded-2xl p-6">
                <h2 className="text-base font-display font-bold text-slate-900 mb-1">Remaining balance over time</h2>
                <p className="text-xs text-slate-400 mb-4">Your loan balance as principal gets paid down, year by year.</p>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={balancePoints} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                            <defs>
                                <linearGradient id="g-balance" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={MORTGAGE_COLORS.interest} stopOpacity={0.25} />
                                    <stop offset="100%" stopColor={MORTGAGE_COLORS.interest} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                            <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => fmtCompact(v, cur)} />
                            <Tooltip
                                formatter={(value) => [fmtCurrency(value, cur), "Balance"]}
                                contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }}
                            />
                            <Area type="monotone" dataKey="balance" name="Balance" stroke={MORTGAGE_COLORS.interest} strokeWidth={2.5} fill="url(#g-balance)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="text-center">
                <button type="button" onClick={() => setShowTable(s => !s)} data-testid="mtg-toggle-table"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
                    {showTable ? "Hide yearly breakdown table" : "View yearly breakdown table"}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showTable ? "rotate-180" : ""}`} />
                </button>
            </div>

            {showTable && (
                <div className="card-elev rounded-2xl p-4 sm:p-6 overflow-x-auto" data-testid="mtg-table">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead>
                            <tr className="text-xs font-bold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                                <th className="py-2 pr-4">Year</th>
                                <th className="py-2 pr-4">Principal</th>
                                <th className="py-2 pr-4">Interest</th>
                                <th className="py-2 pr-4">Taxes &amp; fees</th>
                                <th className="py-2 pr-4">Total paid</th>
                                <th className="py-2">Balance</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {r.yearly.map(y => (
                                <tr key={y.calendarYear}>
                                    <td className="py-2 pr-4 font-semibold text-slate-700">{y.calendarYear}</td>
                                    <td className="py-2 pr-4 text-slate-600 tabular-nums">{fmtCurrency(y.principal, cur)}</td>
                                    <td className="py-2 pr-4 text-slate-600 tabular-nums">{fmtCurrency(y.interest, cur)}</td>
                                    <td className="py-2 pr-4 text-slate-600 tabular-nums">{fmtCurrency(y.taxesFees, cur)}</td>
                                    <td className="py-2 pr-4 font-semibold text-slate-700 tabular-nums">{fmtCurrency(y.principal + y.interest + y.taxesFees, cur)}</td>
                                    <td className="py-2 text-slate-600 tabular-nums">{fmtCurrency(y.endBalance, cur)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
